import { atom, useAtomValue } from "jotai";
import { useCallback, useMemo } from "react";
import { useLocation, useRouteMatch } from "react-router";
import CurrentPublicationEntityAtom from "utilities/atoms/current-publication-entity-atom";
import { PublicationEntityTypeConstants } from "constants/publication-entity-type-constants";
import { siteMap } from "internal-sitemap";
import PublicationMetadataTypeEnum from "models/interfaces/publication-metadata-types";
import { StringUtils } from "@rsm-hcd/javascript-core";
import { ConversationBookviewItemLocation } from "models/interfaces/conversations/conversation-application-context";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import ConversationApplicationContextRecord from "models/view-models/conversations/conversation-application-context-record";
import PublicationTypes from "constants/publication-types";
import CurrentSectionAtom from "utilities/atoms/current-section-atom";

export const CurrentSectionChatAtom = atom<
    ConversationBookviewItemLocation | undefined
>(undefined);
export interface BookviewChatContext {
    code?: string;
    edition?: string;
    applicationContext?: ConversationApplicationContextRecord;
    caapsNumber?: string;
    entityType:
        | keyof typeof PublicationEntityTypeConstants
        | PublicationMetadataTypeEnum
        | undefined;
}

const useBookviewChatContext = () => {
    const currentPublicationEntity = useAtomValue(CurrentPublicationEntityAtom);
    const currentSection = useAtomValue(CurrentSectionAtom);
    const { publication } = useCurrentPublication();
    const { pathname } = useLocation();

    // Manually checking under duress, as it's done throughout the codebase
    const isNecChapterNine =
        pathname.includes("/chapters/9") &&
        publication.type === PublicationTypes.NEC;

    const metadataMatch = useRouteMatch({
        path: siteMap.publications.permalinks.publicationMetadata,
    });
    const chapterTablesMatch = useRouteMatch({
        path: siteMap.publications.permalinks.chapterTables,
    });

    const getLocationInfo = useCallback(() => {
        if (!currentPublicationEntity || !currentSection) {
            return;
        }
        const section = currentSection
            ? {
                  number: currentSection.nfpaLabel,
                  title: currentSection.getTitleAsPlainText(),
              }
            : undefined;
        switch (currentPublicationEntity.type) {
            case PublicationEntityTypeConstants.Chapter:
                return {
                    chapter: {
                        number: currentPublicationEntity.nfpaLabel,
                        title: currentPublicationEntity.entityTitle,
                    },
                    ...(section && { section: section }),
                };
            case PublicationEntityTypeConstants.Annex:
                return {
                    annex: {
                        number: currentPublicationEntity.nfpaLabel,
                        title: currentPublicationEntity.entityTitle,
                    },
                    ...(currentSection && { section: currentSection }),
                };
            case PublicationEntityTypeConstants.Article:
                return {
                    chapter: {
                        number: currentPublicationEntity.chapter?.nfpaLabel,
                        title: currentPublicationEntity.chapter
                            ?.titleAsPlainText,
                    },
                    article: {
                        number: currentPublicationEntity.nfpaLabel,
                        title: currentPublicationEntity.entityTitle,
                    },
                    ...(currentSection && { section: currentSection }),
                };
        }
    }, [currentPublicationEntity, currentSection]);

    const bookviewChatContext: BookviewChatContext = useMemo(() => {
        const location = getLocationInfo();

        const applicationContext =
            new ConversationApplicationContextRecord().buildBookviewContext(
                publication,
                location,
                chapterTablesMatch
            );

        return {
            code: publication.code,
            edition: publication.edition,
            caapsNumber:
                setNfpaLabel(
                    metadataMatch,
                    chapterTablesMatch,
                    currentSection?.nfpaLabel,
                    currentPublicationEntity?.nfpaLabel
                ) ?? "",
            entityType:
                setType(
                    metadataMatch,
                    isNecChapterNine,
                    chapterTablesMatch,
                    currentSection,
                    currentPublicationEntity?.type
                ) ?? "",
            applicationContext: applicationContext,
        };
    }, [
        chapterTablesMatch,
        currentPublicationEntity?.nfpaLabel,
        currentPublicationEntity?.type,
        currentSection,
        getLocationInfo,
        isNecChapterNine,
        metadataMatch,
        publication,
    ]);

    return { bookviewChatContext };
};

const setNfpaLabel = (
    metadataMatch,
    chapterTablesMatch,
    sectionNfpaLabel,
    nfpaLabel
) => {
    if (metadataMatch) {
        return StringUtils.capitalize(metadataMatch?.params["type"]);
    }
    if (chapterTablesMatch) {
        return "Tables";
    }
    if (sectionNfpaLabel) {
        return sectionNfpaLabel;
    }
    return nfpaLabel;
};

const setType = (
    metadataMatch,
    isNecChapterNine,
    chapterTablesMatch,
    section,
    type
) => {
    if (metadataMatch || (isNecChapterNine && section)) {
        return "";
    }
    if (chapterTablesMatch) {
        return StringUtils.capitalize(
            `Chapter ${chapterTablesMatch?.params["chapterNfpaLabel"]}`
        );
    }
    if (section) {
        return "Section";
    }
    return type;
};

export default useBookviewChatContext;
